import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip
} from '@mantine/core';
import { TbPlus, TbRefresh, TbSearch, TbTrash } from 'react-icons/tb';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import {
  buildConnectUiUrl,
  useCreateConnectSession,
  useDeleteIntegrationConnection,
  useIntegrationConnections
} from '../../services/integrations.service';
import {
  INTEGRATIONS_CATALOG,
  getConnectedProviderKeys,
  getConnectionLabel,
  getProviderConnections
} from '../../consts/integrationsCatalog';
import { NewDeleteConfirmModal } from '../../components/NewDeleteConfirmModal';
import SharepointSyncDrawer from './components/SharepointSyncDrawer';

const IntegrationLogo = ({ alt, logoPath }) => {
  return <img src={logoPath} alt={alt} style={{ width: 24, height: 24, objectFit: 'contain' }} />;
};

const getProviderDescription = provider => provider.description || provider.label;
const AVAILABLE_PROVIDER_ORDER = ['sharepoint-online', 'google-drive', 'one-drive'];

export default function IntegrationsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [connectingKey, setConnectingKey] = useState(null);
  const [waitingForConnection, setWaitingForConnection] = useState(false);
  const [selectedSharepointConnection, setSelectedSharepointConnection] = useState(null);
  const [pendingConnectedProviderKey, setPendingConnectedProviderKey] = useState(null);
  const [pendingDeleteConnection, setPendingDeleteConnection] = useState(null);
  const prevConnectionIdsRef = useRef(null);

  const queryClient = useQueryClient();
  const createConnectSession = useCreateConnectSession();
  const deleteConnection = useDeleteIntegrationConnection();

  const {
    data: connectionsData,
    isFetching,
    refetch
  } = useIntegrationConnections({
    queryOptions: { enabled: true }
  });

  const connections = useMemo(() => connectionsData?.connections || [], [connectionsData]);
  const connectedProviderKeys = useMemo(
    () => new Set(getConnectedProviderKeys(connections)),
    [connections]
  );

  useEffect(() => {
    if (!waitingForConnection) return;

    const handleRefocus = async () => {
      const result = await refetch();
      if (prevConnectionIdsRef.current !== null) {
        const newConnections = result.data?.connections || [];
        const addedConnections = newConnections.filter(
          c => !prevConnectionIdsRef.current.has(c.connection_id)
        );
        if (addedConnections.length > 0) {
          const newSharepointConnection =
            pendingConnectedProviderKey === 'sharepoint-online'
              ? addedConnections.find(
                  connection =>
                    (connection.provider_config_key || connection.provider) === 'sharepoint-online'
                )
              : null;

          queryClient.invalidateQueries(['INTEGRATION_CONNECTIONS']);
          prevConnectionIdsRef.current = null;
          setWaitingForConnection(false);
          setPendingConnectedProviderKey(null);

          if (newSharepointConnection) {
            setSelectedSharepointConnection(newSharepointConnection);
          }
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') handleRefocus();
    };

    window.addEventListener('focus', handleRefocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleRefocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [waitingForConnection, refetch, queryClient, pendingConnectedProviderKey]);

  const handleConnect = async providerKey => {
    setConnectingKey(providerKey);
    setPendingConnectedProviderKey(providerKey);
    try {
      const response = await createConnectSession.mutateAsync({
        allowed_integrations: [providerKey]
      });
      const connectLink = buildConnectUiUrl(response);
      if (connectLink) {
        prevConnectionIdsRef.current = new Set(connections.map(c => c.connection_id));
        setWaitingForConnection(true);
        window.open(connectLink, '_blank', 'noopener,noreferrer');
      } else {
        setPendingConnectedProviderKey(null);
      }
    } catch {
      setPendingConnectedProviderKey(null);
      toast.error('Failed to create connect session');
    } finally {
      setConnectingKey(null);
    }
  };

  const handleRefresh = async () => {
    await refetch();
    queryClient.invalidateQueries(['INTEGRATION_CONNECTIONS']);
  };

  const handleDeleteConnection = async reset => {
    if (!pendingDeleteConnection) return;

    try {
      await deleteConnection.mutateAsync({
        provider_key: pendingDeleteConnection.providerKey,
        connection_id: pendingDeleteConnection.connection.connection_id
      });
      queryClient.invalidateQueries(['INTEGRATION_CONNECTIONS']);
      toast.success('Integration connection deleted');
      if (
        selectedSharepointConnection?.connection_id ===
        pendingDeleteConnection.connection.connection_id
      ) {
        setSelectedSharepointConnection(null);
      }
      setPendingDeleteConnection(null);
      reset?.();
      await refetch();
    } catch {
      toast.error('Failed to remove integration connection');
    }
  };

  const sortedIntegrations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = query
      ? INTEGRATIONS_CATALOG.filter(provider => provider.label.toLowerCase().includes(query))
      : INTEGRATIONS_CATALOG;
    const available = filtered
      .filter(provider => provider.canConnect)
      .sort((a, b) => {
        const aIndex = AVAILABLE_PROVIDER_ORDER.indexOf(a.key);
        const bIndex = AVAILABLE_PROVIDER_ORDER.indexOf(b.key);

        if (aIndex === -1 && bIndex === -1) return a.label.localeCompare(b.label);
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;

        return aIndex - bIndex;
      });
    const other = filtered.filter(provider => !provider.canConnect);

    return { available, other };
  }, [connectedProviderKeys, searchQuery]);

  const renderIntegrationCard = provider => {
    const providerConnections = getProviderConnections(connections, provider.key);
    const isConnected = providerConnections.length > 0;
    const previewConnections = providerConnections.slice(0, 2);
    const hiddenCount = Math.max(providerConnections.length - previewConnections.length, 0);

    return (
      <Card
        key={provider.key}
        withBorder
        radius="md"
        p="lg"
        style={{
          backgroundColor: isConnected ? '#f4fbf6' : '#fafafa',
          borderColor: isConnected ? '#cfead6' : '#eceef2',
          flex: '1 1 300px',
          maxWidth: 420,
          minHeight: 252,
          display: 'flex'
        }}
      >
        <Stack gap="md" justify="space-between" style={{ flex: 1 }}>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: '#f5f6f8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <IntegrationLogo alt={provider.label} logoPath={provider.logoPath} />
              </div>

              <div>
                <Text fw={600} size="sm">
                  {provider.label}
                </Text>
                <Text size="xs" c="dimmed">
                  {getProviderDescription(provider)}
                </Text>
              </div>
            </Group>

            <Badge
              variant="light"
              color={isConnected ? 'green' : provider.canConnect ? 'blue' : 'gray'}
              style={{ minWidth: 92, justifyContent: 'center', whiteSpace: 'nowrap' }}
            >
              {isConnected ? 'Connected' : provider.canConnect ? 'Available' : 'Locked'}
            </Badge>
          </Group>

          {isConnected ? (
            <Stack gap="xs">
              {previewConnections.map(connection => (
                <Card
                  key={connection.connection_id}
                  withBorder
                  radius="md"
                  p="sm"
                  style={{ backgroundColor: '#fff' }}
                >
                  <Text fw={500} size="sm">
                    {getConnectionLabel(connection)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {connection.connection_id}
                  </Text>
                  {provider.key === 'sharepoint-online' ? (
                    <Stack gap={6} mt="xs">
                      {(connection?.metadata?.sharepoint_sync_config?.permitted_folders || [])
                        .length > 0 ? (
                        <Text size="xs" c="dimmed">
                          Permissions:{' '}
                          {(connection.metadata.sharepoint_sync_config.permitted_folders || [])
                            .slice(0, 2)
                            .map(folder => folder.name)
                            .join(', ')}
                          {(connection.metadata.sharepoint_sync_config.permitted_folders || [])
                            .length > 2
                            ? ` +${
                                (connection.metadata.sharepoint_sync_config.permitted_folders || [])
                                  .length - 2
                              } more`
                            : ''}
                        </Text>
                      ) : (
                        <Text size="xs" c="dimmed">
                          No folder permissions configured yet.
                        </Text>
                      )}
                      <Group justify="space-between" align="center">
                        <Badge
                          variant="light"
                          color={
                            (connection?.metadata?.sharepoint_sync_config?.permitted_folders || [])
                              .length > 0
                              ? 'green'
                              : 'red'
                          }
                        >
                          {(connection?.metadata?.sharepoint_sync_config?.permitted_folders || [])
                            .length > 0
                            ? `${(connection?.metadata?.sharepoint_sync_config?.permitted_folders || []).length} permission${
                                (
                                  connection?.metadata?.sharepoint_sync_config?.permitted_folders ||
                                  []
                                ).length > 1
                                  ? 's'
                                  : ''
                              }`
                            : 'Permissions required'}
                        </Badge>
                        <Button
                          size="xs"
                          variant="subtle"
                          onClick={() => setSelectedSharepointConnection(connection)}
                        >
                          Permissions
                        </Button>
                      </Group>
                      <Group justify="flex-end">
                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          leftSection={<TbTrash size={14} />}
                          onClick={() =>
                            setPendingDeleteConnection({ connection, providerKey: provider.key })
                          }
                        >
                          Delete
                        </Button>
                      </Group>
                    </Stack>
                  ) : (
                    <Group justify="flex-end" mt="xs">
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        leftSection={<TbTrash size={14} />}
                        onClick={() =>
                          setPendingDeleteConnection({ connection, providerKey: provider.key })
                        }
                      >
                        Delete
                      </Button>
                    </Group>
                  )}
                </Card>
              ))}
              {hiddenCount > 0 ? (
                <Text size="xs" c="dimmed">
                  +{hiddenCount} more connected source{hiddenCount > 1 ? 's' : ''}
                </Text>
              ) : null}
              <Button
                size="xs"
                variant="light"
                leftSection={<TbPlus size={14} />}
                loading={connectingKey === provider.key}
                onClick={() => handleConnect(provider.key)}
              >
                Add another source
              </Button>
            </Stack>
          ) : provider.canConnect ? (
            <Button
              size="sm"
              variant="light"
              leftSection={<TbPlus size={16} />}
              loading={connectingKey === provider.key}
              onClick={() => handleConnect(provider.key)}
            >
              Connect {provider.label}
            </Button>
          ) : (
            <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
              {getProviderDescription(provider)}
            </Text>
          )}
        </Stack>
      </Card>
    );
  };

  const renderOtherIntegrationCard = provider => {
    return (
      <Card
        key={provider.key}
        withBorder
        radius="md"
        p="md"
        style={{
          backgroundColor: '#fafafa',
          borderColor: '#eceef2',
          flex: '1 1 260px',
          maxWidth: 320,
          minHeight: 128,
          display: 'flex'
        }}
      >
        <Stack gap="xs" justify="flex-start" style={{ flex: 1 }}>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: '#f5f6f8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <IntegrationLogo alt={provider.label} logoPath={provider.logoPath} />
              </div>

              <div>
                <Text fw={600} size="sm">
                  {provider.label}
                </Text>
              </div>
            </Group>

            <Badge
              variant="light"
              color="gray"
              style={{ minWidth: 72, justifyContent: 'center', whiteSpace: 'nowrap' }}
            >
              Locked
            </Badge>
          </Group>

          <Text size="sm" c="dimmed" style={{ lineHeight: 1.4 }}>
            {getProviderDescription(provider)}
          </Text>
        </Stack>
      </Card>
    );
  };

  return (
    <Stack p="md" gap="lg" w="100%">
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
        <div>
          <Title order={3}>Integrations</Title>
          <Text c="dimmed" size="sm">
            Connected sources are available below. Other integrations are listed beneath the
            available sources.
          </Text>
        </div>
        <Tooltip label="Refresh connections">
          <ActionIcon variant="subtle" color="gray" onClick={handleRefresh} loading={isFetching}>
            <TbRefresh size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <TextInput
        placeholder="Search integrations…"
        leftSection={<TbSearch size={16} />}
        value={searchQuery}
        onChange={e => setSearchQuery(e.currentTarget.value)}
        w={360}
        miw={280}
      />

      {isFetching ? (
        <Loader size="sm" />
      ) : (
        <Stack gap="xl">
          <div>
            <Text fw={600} size="sm" tt="uppercase" c="dimmed" mb="sm">
              Available integrations
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {sortedIntegrations.available.map(renderIntegrationCard)}
            </div>
          </div>

          <div>
            <Text fw={600} size="sm" tt="uppercase" c="dimmed" mb="sm">
              Other integrations
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {sortedIntegrations.other.map(renderOtherIntegrationCard)}
            </div>
          </div>
        </Stack>
      )}

      <SharepointSyncDrawer
        opened={Boolean(selectedSharepointConnection)}
        connection={selectedSharepointConnection}
        onClose={() => setSelectedSharepointConnection(null)}
        onSaved={() => {
          refetch();
        }}
      />

      <NewDeleteConfirmModal
        isOpen={Boolean(pendingDeleteConnection)}
        onClose={() => setPendingDeleteConnection(null)}
        onConfirm={handleDeleteConnection}
        isLoading={deleteConnection.isLoading}
        title="Delete integration"
        description={`This will disconnect ${pendingDeleteConnection?.connection ? getConnectionLabel(pendingDeleteConnection.connection) : 'this source'} from Datox.`}
        submitLabel="Delete"
        cancelLabel="Cancel"
        type="delete"
      />
    </Stack>
  );
}
